import { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { Navbar, Footer } from '../components/Layout';
import { useTranslation } from '../context/TranslationContext';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Avatar, AvatarImage, AvatarFallback } from '../components/ui/avatar';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import { Users, Award, Search, ChevronLeft, ChevronRight } from 'lucide-react';

import { getApiBaseUrl } from '@/lib/apiBase';
const API_URL = getApiBaseUrl();
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

const StudentDirectoryPage = () => {
  const { t, tFormat } = useTranslation();
  const [data, setData] = useState({ items: [], total: 0, page: 1, pages: 1 });
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);

  const fetchDirectory = useCallback(async (pageNum, searchTerm) => {
    setLoading(true);
    try {
      const res = await axios.get(`${API_URL}/students/directory`, {
        params: { page: pageNum, limit: 12, search: searchTerm }
      });
      setData(res.data);
    } catch (err) {
      console.error(err);
      setData({ items: [], total: 0, page: 1, pages: 1 });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchDirectory(page, search);
  }, [page, fetchDirectory]);

  const handleSearchSubmit = (e) => {
    e.preventDefault();
    setPage(1);
    fetchDirectory(1, search);
  };

  return (
    <div className="min-h-screen bg-background" data-testid="student-directory-page">
      <Navbar />

      <main className="pt-20 pb-12">
        <div className="max-w-6xl mx-auto px-6 md:px-12 py-12">
          {/* Header */}
          <div className="text-center mb-12">
            <div className="w-16 h-16 bg-accent/10 rounded-full flex items-center justify-center mx-auto mb-4">
              <Users className="w-8 h-8 text-accent" />
            </div>
            <h1 className="text-4xl font-serif font-medium mb-2">
              {t('students_title', 'Our Students')}
            </h1>
            <p className="text-muted-foreground max-w-xl mx-auto">
              {t('students_subtitle', 'Meet the makers, designers, and craftspeople learning with THACHARI')}
            </p>
          </div>

          {/* Search */}
          <form onSubmit={handleSearchSubmit} className="max-w-md mx-auto mb-10 flex gap-2">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <Input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder={t('students_search_placeholder', 'Search by name...')}
                className="pl-9"
                data-testid="student-search-input"
              />
            </div>
            <Button type="submit" variant="outline" data-testid="student-search-btn">
              {t('students_search_btn', 'Search')}
            </Button>
          </form>

          {/* Grid */}
          {loading ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {[1, 2, 3, 4, 5, 6].map((i) => (
                <div key={i} className="h-56 bg-muted rounded-xl animate-pulse" />
              ))}
            </div>
          ) : data.items.length === 0 ? (
            <Card>
              <CardContent className="p-12 text-center">
                <Users className="w-12 h-12 mx-auto text-muted-foreground mb-4" />
                <h3 className="text-lg font-semibold mb-2">
                  {t('students_empty_title', 'No students found')}
                </h3>
                <p className="text-muted-foreground">
                  {t('students_empty_hint', 'Try a different search term.')}
                </p>
              </CardContent>
            </Card>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {data.items.map((student) => (
                <Card key={student.id} className="hover:shadow-md transition-all" data-testid={`student-card-${student.id}`}>
                  <CardContent className="p-6 flex flex-col items-center text-center">
                    <Avatar className="w-20 h-20 mb-4">
                      <AvatarImage src={student.photo_url ? `${BACKEND_URL}${student.photo_url}` : undefined} alt={student.name} />
                      <AvatarFallback className="bg-accent/10 text-accent text-xl font-serif">
                        {student.name?.charAt(0)?.toUpperCase() || '?'}
                      </AvatarFallback>
                    </Avatar>
                    <h3 className="font-serif text-lg font-medium mb-1">{student.name}</h3>
                    {student.bio && (
                      <p className="text-sm text-muted-foreground mb-3 line-clamp-3">{student.bio}</p>
                    )}
                    <div className="flex items-center gap-1 text-sm text-accent mb-3">
                      <Award className="w-4 h-4" />
                      <span>
                        {tFormat('students_cert_count', { n: student.certificate_count }, '{n} certificates earned')}
                      </span>
                    </div>
                    {student.badges?.length > 0 && (
                      <div className="flex flex-wrap justify-center gap-1 mb-3">
                        {student.badges.slice(0, 3).map((badge) => (
                          <Badge key={badge} variant="secondary" className="text-xs">
                            {badge.replace(/_/g, ' ')}
                          </Badge>
                        ))}
                      </div>
                    )}
                    {student.project_ids?.length > 0 && (
                      <Link to="/projects" className="text-sm text-accent hover:underline">
                        {tFormat('students_view_projects', { n: student.project_ids.length }, 'View {n} project(s)')}
                      </Link>
                    )}
                  </CardContent>
                </Card>
              ))}
            </div>
          )}

          {/* Pagination */}
          {data.pages > 1 && (
            <div className="flex items-center justify-center gap-4 mt-10">
              <Button
                variant="outline"
                size="icon"
                disabled={page <= 1}
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                data-testid="students-prev-page"
              >
                <ChevronLeft className="w-4 h-4" />
              </Button>
              <span className="text-sm text-muted-foreground">
                {tFormat('students_page_of', { page: data.page, pages: data.pages }, 'Page {page} of {pages}')}
              </span>
              <Button
                variant="outline"
                size="icon"
                disabled={page >= data.pages}
                onClick={() => setPage((p) => Math.min(data.pages, p + 1))}
                data-testid="students-next-page"
              >
                <ChevronRight className="w-4 h-4" />
              </Button>
            </div>
          )}
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default StudentDirectoryPage;
